import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User } from '@/types'
import {
    BrandingConfig,
    DEFAULT_BRANDING,
    createBrandingFromUser,
    applyBranding,
    saveBrandingToStorage,
    loadBrandingFromStorage,
    clearBrandingFromStorage,
    resetToDefaultBranding
} from '@/utils/branding'
import { whiteLabelService, WhiteLabelDomain } from '@/services/whiteLabelService'

interface BrandingContextType {
    branding: BrandingConfig
    isCustomBranding: boolean
    isLoading: boolean
    currentDomain: string
    merchantData?: {
        id: string
        name: string
        company_name: string
    } | null
    currentWhiteLabelDomain?: WhiteLabelDomain | null
    updateBranding: (newBranding: Partial<BrandingConfig>) => void
    resetBranding: () => void
    toggleCustomBranding: () => void
    applyUserBranding: (user: User) => void
    refreshMerchantBranding: () => Promise<void>
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined)

interface BrandingProviderProps {
    children: ReactNode
    user?: User
}

export function BrandingProvider({ children, user }: BrandingProviderProps) {
    const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING)
    const [isCustomBranding, setIsCustomBranding] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [currentDomain, setCurrentDomain] = useState('')
    const [merchantData, setMerchantData] = useState<{ id: string; name: string; company_name: string } | null>(null)
    const [currentWhiteLabelDomain, setCurrentWhiteLabelDomain] = useState<WhiteLabelDomain | null>(null)

    // Get current domain from browser
    useEffect(() => {
        if (typeof window !== 'undefined') {
            setCurrentDomain(window.location.hostname)
        }
    }, [])

    // Fetch merchant branding from current domain
    const fetchMerchantBrandingFromDomain = async () => {
        if (!currentDomain || currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
            return
        }

        try {
            console.log(`[BrandingContext] Fetching merchant info for domain: ${currentDomain}`)

            // Try to get merchant info from white-label domain endpoint
            const merchantInfo = await whiteLabelService.getMerchantInfoByDomain(currentDomain)

            if (merchantInfo) {
                console.log('[BrandingContext] Merchant info found:', merchantInfo)

                // Set merchant data
                setMerchantData({
                    id: merchantInfo.merchant,
                    name: merchantInfo.merchant_name,
                    company_name: merchantInfo.merchant_name
                })

                // Set current domain config
                if (merchantInfo.primary_domain) {
                    setCurrentWhiteLabelDomain(merchantInfo.primary_domain)
                }

                // Create branding from merchant info
                const merchantBranding: BrandingConfig = {
                    brandName: merchantInfo.branding?.company_display_name || merchantInfo.merchant_name,
                    primaryColor: merchantInfo.branding?.primary_color || DEFAULT_BRANDING.primaryColor,
                    secondaryColor: merchantInfo.branding?.secondary_color || DEFAULT_BRANDING.secondaryColor,
                    logoUrl: merchantInfo.branding?.logo || DEFAULT_BRANDING.logoUrl,
                    faviconUrl: merchantInfo.branding?.favicon || DEFAULT_BRANDING.faviconUrl,
                    supportEmail: merchantInfo.branding?.support_email || DEFAULT_BRANDING.supportEmail,
                    supportPhone: merchantInfo.branding?.support_phone || DEFAULT_BRANDING.supportPhone,
                    pageTitle: merchantInfo.branding?.page_title || DEFAULT_BRANDING.pageTitle,
                    metaDescription: merchantInfo.branding?.meta_description || DEFAULT_BRANDING.metaDescription,
                    primaryColorLight: '',
                    primaryColorDark: '',
                    secondaryColorLight: '',
                    secondaryColorDark: '',
                    backgroundColor: '',
                    surfaceColor: '',
                    textColor: '',
                    textSecondaryColor: '',
                    theme: 'dark',
                    borderRadius: '',
                    fontFamily: ''
                }

                setBranding(merchantBranding)
                setIsCustomBranding(true)
                applyBranding(merchantBranding)

                // Save to localStorage
                saveBrandingToStorage(merchantBranding)
            } else {
                // No merchant found for this domain - ensure default branding is applied
                console.log('[BrandingContext] No merchant found for domain, using default branding')
                setBranding(DEFAULT_BRANDING)
                setIsCustomBranding(false)
                applyBranding(DEFAULT_BRANDING)
            }
        } catch (error) {
            console.log('[BrandingContext] Error fetching merchant info, using default branding', error)
            // Ensure default branding is applied on error
            setBranding(DEFAULT_BRANDING)
            setIsCustomBranding(false)
            applyBranding(DEFAULT_BRANDING)
        }
    }

    // Initialize branding on mount
    useEffect(() => {
        const initializeBranding = async () => {
            try {
                // First try to load domain-specific branding
                if (currentDomain && currentDomain !== 'localhost' && currentDomain !== '127.0.0.1') {
                    await fetchMerchantBrandingFromDomain()
                } else {
                    // Try to load from localStorage first
                    const storedBranding = loadBrandingFromStorage()

                    if (storedBranding) {
                        setBranding(storedBranding)
                        setIsCustomBranding(true)
                        applyBranding(storedBranding)
                    } else if (user) {
                        // Create branding from user data
                        const userBranding = createBrandingFromUser(user)
                        setBranding(userBranding)
                        setIsCustomBranding(userBranding.brandName !== DEFAULT_BRANDING.brandName)
                        applyBranding(userBranding)
                    } else {
                        // Use default branding
                        setBranding(DEFAULT_BRANDING)
                        setIsCustomBranding(false)
                        applyBranding(DEFAULT_BRANDING)
                    }
                }
            } catch (error) {
                console.error('Failed to initialize branding:', error)
                setBranding(DEFAULT_BRANDING)
                setIsCustomBranding(false)
                applyBranding(DEFAULT_BRANDING)
            } finally {
                setIsLoading(false)
            }
        }

        initializeBranding()
    }, [currentDomain])

    // Update branding when user changes
    useEffect(() => {
        if (user && !isLoading && !merchantData) {
            applyUserBranding(user)
        }
    }, [user, isLoading, merchantData])

    const updateBranding = (newBranding: Partial<BrandingConfig>) => {
        const updatedBranding = { ...branding, ...newBranding }
        setBranding(updatedBranding)
        setIsCustomBranding(true)

        // Apply to DOM
        applyBranding(updatedBranding)

        // Save to localStorage
        saveBrandingToStorage(updatedBranding)
    }

    const resetBranding = () => {
        setBranding(DEFAULT_BRANDING)
        setIsCustomBranding(false)
        setMerchantData(null)
        setCurrentWhiteLabelDomain(null)

        // Apply default branding
        resetToDefaultBranding()

        // Clear from localStorage
        clearBrandingFromStorage()
    }

    const toggleCustomBranding = () => {
        if (isCustomBranding) {
            resetBranding()
        } else {
            setIsCustomBranding(true)
            saveBrandingToStorage(branding)
        }
    }

    const applyUserBranding = (user: User) => {
        const userBranding = createBrandingFromUser(user)
        setBranding(userBranding)
        setIsCustomBranding(userBranding.brandName !== DEFAULT_BRANDING.brandName)
        applyBranding(userBranding)

        // Save to localStorage if it's custom branding
        if (userBranding.brandName !== DEFAULT_BRANDING.brandName) {
            saveBrandingToStorage(userBranding)
        }
    }

    const refreshMerchantBranding = async () => {
        await fetchMerchantBrandingFromDomain()
    }

    const value: BrandingContextType = {
        branding,
        isCustomBranding,
        isLoading,
        currentDomain,
        merchantData,
        currentWhiteLabelDomain,
        updateBranding,
        resetBranding,
        toggleCustomBranding,
        applyUserBranding,
        refreshMerchantBranding,
    }

    return (
        <BrandingContext.Provider value={value}>
            {children}
        </BrandingContext.Provider>
    )
}

export function useBranding(): BrandingContextType {
    const context = useContext(BrandingContext)
    if (context === undefined) {
        throw new Error('useBranding must be used within a BrandingProvider')
    }
    return context
}

// Safe hook that returns default branding if context not available
export function useBrandingOrDefault(): BrandingContextType {
    const context = useContext(BrandingContext)
    if (context === undefined) {
        // Return default branding context
        return {
            branding: DEFAULT_BRANDING,
            isCustomBranding: false,
            isLoading: false,
            currentDomain: '',
            merchantData: null,
            currentWhiteLabelDomain: null,
            updateBranding: () => { },
            resetBranding: () => { },
            toggleCustomBranding: () => { },
            applyUserBranding: () => { },
            refreshMerchantBranding: async () => { },
        }
    }
    return context
}

// Hook for accessing branding configuration
export function useBrandingConfig(): BrandingConfig {
    const { branding } = useBranding()
    return branding
}

// Hook for checking if branding is customized
export function useIsCustomBranding(): boolean {
    const { isCustomBranding } = useBranding()
    return isCustomBranding
}

// Hook for updating branding
export function useUpdateBranding() {
    const { updateBranding } = useBranding()
    return updateBranding
}

// Hook for resetting branding
export function useResetBranding() {
    const { resetBranding } = useBranding()
    return resetBranding
}

// NEW: Hook for accessing merchant data
export function useMerchantData() {
    const { merchantData, currentDomain } = useBranding()
    return { merchantData, currentDomain }
}

// NEW: Hook for refreshing merchant branding
export function useRefreshMerchantBranding() {
    const { refreshMerchantBranding } = useBranding()
    return refreshMerchantBranding
}

// NEW: Hook for accessing current white-label domain
export function useWhiteLabelDomain() {
    const { currentWhiteLabelDomain } = useBranding()
    return currentWhiteLabelDomain
}
